import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, AppBar, Box, Button, CircularProgress, Container, Paper, Stack, TextField, Toolbar, Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '../api/axios';

/* Gratitud: escribe una queja → n8n la clasifica (🟢 fricción, 🔵 acción, 🔴 daño) → confirmas y anotas cómo te sientes.
   Las quejas viven en Notion (página "Gratitud"); la lista "Palabras rojas" se edita allá. */
const TIPOS = {
    Verde: { emoji: '🟢', nombre: 'Fricción normal', color: '#1e8449', fondo: '#eafaf1', titulo: 'Lo que recibes a cambio' },
    Azul: { emoji: '🔵', nombre: 'Lo puedes cambiar', color: '#2a78d6', fondo: '#eaf2fb', titulo: 'Una acción para hoy' },
    Rojo: { emoji: '🔴', nombre: 'Te están haciendo daño', color: '#c0392b', fondo: '#fdecea', titulo: 'Esto se atiende' },
};
const MSG_ROJO = 'Esto no se agradece, se atiende. No tienes que darle la vuelta. Considera poner un límite claro y hablarlo con alguien de confianza o con un profesional. Si estás en peligro ahora mismo, llama al 110.';
const DAY = 86400000;

function Escala({ value, onChange, disabled }) {
    return (
        <Stack direction="row" spacing={1}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Button key={n} variant={value === n ? 'contained' : 'outlined'} onClick={() => onChange(n)} disabled={disabled}
                    sx={{ flex: 1, minWidth: 0, py: 1.5, fontSize: 18, fontWeight: 700 }}>{n}</Button>
            ))}
        </Stack>
    );
}

function Semana({ quejas }) {
    const desde = Date.now() - 7 * DAY;
    const sem = quejas.filter((q) => q.fecha && new Date(q.fecha).getTime() >= desde);
    const contables = sem.filter((q) => q.tipo !== 'Rojo');
    const convertidas = contables.filter((q) => q.convertida).length;
    const rojas = sem.filter((q) => q.tipo === 'Rojo').length;
    const conAlivio = sem.filter((q) => q.estres_antes && q.estres_despues);
    const alivio = conAlivio.length ? conAlivio.reduce((s, q) => s + (q.estres_antes - q.estres_despues), 0) / conAlivio.length : null;
    const cats = {};
    sem.forEach((q) => { cats[q.categoria] = (cats[q.categoria] || 0) + 1; });
    const top = Object.entries(cats).sort((a, b) => b[1] - a[1]);
    const max = top.length ? top[0][1] : 1;

    return (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Tus últimos 7 días</Typography>
            {!sem.length ? (
                <Typography variant="body2" color="text.secondary">Aún no hay quejas esta semana.</Typography>
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: 30, lineHeight: 1.1 }}>{contables.length ? `${Math.round((convertidas / contables.length) * 100)}%` : '–'}</Typography>
                            <Typography variant="body2" color="text.secondary">convertidas en gratitud o acción ({convertidas} de {contables.length})</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: 30, lineHeight: 1.1 }}>{alivio === null ? '–' : `${alivio > 0 ? '−' : ''}${Math.abs(alivio).toFixed(1)}`}</Typography>
                            <Typography variant="body2" color="text.secondary">de estrés en promedio después de darle la vuelta</Typography>
                        </Box>
                    </Box>
                    <Stack spacing={0.75} sx={{ mt: 2.5 }}>
                        {top.map(([c, n]) => (
                            <Stack key={c} direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ width: 72 }}>{c}</Typography>
                                <Box sx={{ flex: 1, bgcolor: '#eef1f5', borderRadius: 1, height: 10 }}>
                                    <Box sx={{ width: `${(n / max) * 100}%`, bgcolor: '#6da7ec', borderRadius: 1, height: 10 }} />
                                </Box>
                                <Typography variant="body2" sx={{ width: 20, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                    {rojas > 0 && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            🔴 {rojas === 1 ? 'Hubo 1 situación' : `Hubo ${rojas} situaciones`} de daño esta semana. No cuentan como quejas por convertir: son señales para actuar o pedir apoyo.
                        </Alert>
                    )}
                </>
            )}
        </Paper>
    );
}

export default function Gratitud() {
    const navigate = useNavigate();
    const [quejas, setQuejas] = useState([]);
    const [paso, setPaso] = useState(1); // 1 escribir · 2 resultado · 3 cómo te sientes · 4 listo
    const [texto, setTexto] = useState('');
    const [estres, setEstres] = useState(0);
    const [q, setQ] = useState(null);
    const [propia, setPropia] = useState('');
    const [despues, setDespues] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const cargar = useCallback(async () => {
        try { const { data } = await api.get('/gratitud/'); setQuejas(data); } catch (e) { setError(e?.response?.data?.message || 'No se pudieron leer tus quejas de Notion.'); }
    }, []);
    useEffect(() => { cargar(); }, [cargar]);

    const enviar = async () => {
        setBusy(true); setError('');
        try {
            const { data } = await api.post('/gratitud/', { texto, estres });
            setQ(data); setPropia(''); setPaso(2);
        } catch (e) { setError(e?.response?.data?.message || 'No se pudo clasificar. Intenta otra vez.'); }
        finally { setBusy(false); }
    };
    const cambiarTipo = (tipo) => setQ({ ...q, tipo, respuesta: tipo === q.tipoOriginal ? q.respuestaOriginal : '', cambiado: tipo !== (q.tipoOriginal || q.tipo) });
    useEffect(() => { if (q && !q.tipoOriginal) setQ({ ...q, tipoOriginal: q.tipo, respuestaOriginal: q.respuesta }); }, [q]);

    const guardar = async (convertida) => {
        setBusy(true); setError('');
        const body = { estres_despues: despues };
        if (q.cambiado) { body.tipo = q.tipo; body.respuesta = q.tipo === 'Rojo' ? MSG_ROJO : propia; }
        if (q.tipo !== 'Rojo') body.convertida = convertida;
        try { await api.patch(`/gratitud/${q.id}`, body); setPaso(4); cargar(); }
        catch (e) { setError(e?.response?.data?.message || 'No se pudo guardar en Notion.'); }
        finally { setBusy(false); }
    };
    const otra = () => { setPaso(1); setTexto(''); setEstres(0); setQ(null); setDespues(0); setError(''); };

    const logout = () => { localStorage.removeItem('accessToken'); localStorage.removeItem('refreshToken'); localStorage.removeItem('user'); navigate('/login'); };
    const t = q ? TIPOS[q.tipo] || TIPOS.Rojo : null;
    const recientes = useMemo(() => [...quejas].filter((x) => x.fecha).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).slice(0, 5), [quejas]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0b1220' }}>
                <Toolbar sx={{ gap: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>Gratitud</Typography>
                    <Button color="inherit" href="/panel/estudio">Estudio</Button>
                    <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>Salir</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Stack spacing={3}>
                    {error && <Alert severity="error">{error}</Alert>}

                    {paso === 1 && (
                        <Paper variant="outlined" sx={{ p: 2.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>¿Qué te está molestando?</Typography>
                            <TextField multiline minRows={3} fullWidth value={texto} onChange={(e) => setTexto(e.target.value)}
                                placeholder="Ej.: me choca pagar la luz, siempre sube" sx={{ '& textarea': { fontSize: 17 } }} />
                            <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>¿Cuánto te estresa? (1 poco · 5 mucho)</Typography>
                            <Escala value={estres} onChange={setEstres} disabled={busy} />
                            <Button variant="contained" size="large" fullWidth onClick={enviar} disabled={busy || !texto.trim() || !estres}
                                startIcon={busy && <CircularProgress size={18} color="inherit" />} sx={{ mt: 3, py: 1.5, fontSize: 16 }}>
                                {busy ? 'Pensándolo contigo…' : 'Darle la vuelta'}
                            </Button>
                        </Paper>
                    )}

                    {paso >= 2 && paso <= 3 && q && (
                        <Paper variant="outlined" sx={{ p: 2.5, borderColor: t.color, borderWidth: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>“{texto}”</Typography>
                            <Box sx={{ bgcolor: t.fondo, borderRadius: 2, p: 2 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: 22, color: t.color }}>{t.emoji} {t.nombre}</Typography>
                                <Typography variant="body2" color="text.secondary">{q.categoria}{q.tipo === 'Rojo' && q.motivo ? ` · ${q.motivo}` : ''}</Typography>
                                <Typography sx={{ fontWeight: 700, mt: 1.5 }}>{t.titulo}</Typography>
                                {q.respuesta
                                    ? <Typography sx={{ fontSize: 17, lineHeight: 1.5 }}>{q.respuesta}</Typography>
                                    : q.tipo === 'Rojo'
                                        ? <Typography sx={{ fontSize: 17, lineHeight: 1.5 }}>{MSG_ROJO}</Typography>
                                        : <TextField fullWidth multiline minRows={2} value={propia} onChange={(e) => setPropia(e.target.value)} sx={{ mt: 1, bgcolor: '#fff' }}
                                            placeholder={q.tipo === 'Verde' ? '¿Qué recibes a cambio? Escríbelo tú.' : '¿Qué acción pequeña puedes hacer hoy?'} />}
                            </Box>

                            {paso === 2 && (
                                <>
                                    <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>¿Es el tipo correcto?</Typography>
                                    <Stack direction="row" spacing={1}>
                                        {Object.entries(TIPOS).map(([k, v]) => (
                                            <Button key={k} variant={q.tipo === k ? 'contained' : 'outlined'} onClick={() => cambiarTipo(k)}
                                                sx={{ flex: 1, minWidth: 0, py: 1.25, textTransform: 'none', ...(q.tipo === k && { bgcolor: v.color, '&:hover': { bgcolor: v.color } }) }}>
                                                {v.emoji} {k}
                                            </Button>
                                        ))}
                                    </Stack>
                                    <Button variant="contained" size="large" fullWidth onClick={() => setPaso(3)}
                                        disabled={q.cambiado && q.tipo !== 'Rojo' && !propia.trim()} sx={{ mt: 2.5, py: 1.5, fontSize: 16 }}>Sí, sigamos</Button>
                                </>
                            )}

                            {paso === 3 && (
                                <>
                                    <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>¿Cómo te sientes ahora? (antes: {estres})</Typography>
                                    <Escala value={despues} onChange={setDespues} disabled={busy} />
                                    {q.tipo === 'Rojo' ? (
                                        <Button variant="contained" size="large" fullWidth onClick={() => guardar(false)} disabled={busy || !despues} sx={{ mt: 2.5, py: 1.5 }}>Guardar</Button>
                                    ) : (
                                        <>
                                            <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>¿Le diste la vuelta?</Typography>
                                            <Stack direction="row" spacing={1.5}>
                                                <Button variant="contained" size="large" onClick={() => guardar(true)} disabled={busy || !despues} sx={{ flex: 1, py: 1.5 }}>Sí 🙌</Button>
                                                <Button variant="outlined" size="large" onClick={() => guardar(false)} disabled={busy || !despues} sx={{ flex: 1, py: 1.5 }}>Todavía no</Button>
                                            </Stack>
                                        </>
                                    )}
                                </>
                            )}
                        </Paper>
                    )}

                    {paso === 4 && (
                        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 40 }}>🌳</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Guardado</Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>{despues < estres ? `Bajaste de ${estres} a ${despues}.` : 'Gracias por registrarlo, también cuenta.'}</Typography>
                            <Button variant="contained" size="large" onClick={otra} sx={{ py: 1.5, px: 4 }}>Registrar otra</Button>
                        </Paper>
                    )}

                    <Semana quejas={quejas} />

                    {recientes.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2.5 }}>
                            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Últimas</Typography>
                            <Stack spacing={1.5}>
                                {recientes.map((x) => (
                                    <Box key={x.id}>
                                        <Typography variant="body2"><b>{(TIPOS[x.tipo] || {}).emoji} {x.categoria}</b> · {x.texto}</Typography>
                                        {x.respuesta && x.tipo !== 'Rojo' && <Typography variant="body2" color="text.secondary">→ {x.respuesta}</Typography>}
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </Container>
        </Box>
    );
}
